import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAllAnswers } from "../api/Users";
import { toast } from "react-toastify";
import { Button } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const CheckListReport = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [answerData, setAnswerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    getAllAnswers(id)
      .then((res) => {
        if (res.status === "success") {
          setAnswerData(res.data.checklist);
        } else {
          toast.error("Failed to fetch questions");
        }
        setIsLoading(false);
      })
      .catch((error) => {
        toast.error("Something went wrong");
        setIsLoading(false);
      });
  }, []);

  const renderTableData = () => {
    if (answerData.length === 0) return null;

    const answers = answerData.map((val) => val.answers.split(","));

    return answers[0].map((_, index) => (
      <TableRow key={index}>
        {answers.map((answer, rowIndex) => (
          <TableCell key={rowIndex} align="center">
            {answer[index]}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // const convertToExcel = () => {
  //   const workbook = new ExcelJS.Workbook();
  //   const worksheet = workbook.addWorksheet("Response Report");

  //   worksheet.mergeCells("A1:B1");
  //   const titleRow = worksheet.getRow(1);
  //   titleRow.getCell(1).value = "Response Report";
  //   titleRow.getCell(1).font = { bold: true, size: 16 };
  //   titleRow.getCell(1).alignment = { horizontal: "center" };

  //   const headers = answerData.map((answer) => answer.question);
  //   const headerRow = worksheet.addRow(headers);
  //   headerRow.eachCell((cell) => {
  //     cell.fill = {
  //       type: "pattern",
  //       pattern: "solid",
  //       fgColor: { argb: "003366" },
  //     };
  //     cell.font = {
  //       color: { argb: "FFFFFF" },
  //       bold: true,
  //     };
  //   });

  //   const answers = answerData.map((answer) => answer.answers.split(","));

  //   for (let i = 0; i < answers[0].length; i++) {
  //     const rowData = [];
  //     for (let j = 0; j < answers.length; j++) {
  //       rowData.push(answers[j][i]);
  //     }
  //     worksheet.addRow(rowData);
  //   }

  //   workbook.creator = "Your Name";
  //   workbook.lastModifiedBy = "Your Name";
  //   workbook.created = new Date();
  //   workbook.modified = new Date();

  //   workbook.xlsx.writeBuffer().then((buffer) => {
  //     const blob = new Blob([buffer], {
  //       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     });
  //     const fileName = "response_report.xlsx";
  //     saveAs(blob, fileName);
  //   });
  // };

  const convertToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Response Report");

    // Add title
    worksheet.mergeCells("A1:B1");
    const titleRow = worksheet.getRow(1);
    titleRow.getCell(1).value = "Response Report";
    titleRow.getCell(1).font = { bold: true, size: 16 };
    titleRow.getCell(1).alignment = { horizontal: "center" };

    // Dynamic headers based on question
    const headers = answerData.map((answer) => answer.question);
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "003366" },
      };
      cell.font = {
        color: { argb: "FFFFFF" },
        bold: true,
      };
    });

    // Add data
    const maxAnswers = Math.max(
      ...answerData.map((answer) => answer.answers.split(",").length)
    );
    for (let i = 0; i < maxAnswers; i++) {
      const rowData = [];
      answerData.forEach((answer) => {
        const answers = answer.answers.split(",");
        if (answers[i] && answer.question_type === 1) {
          // Handle image insertion
          const imageId = workbook.addImage({
            base64: answers[i],
            extension: "png",
          });
          const rowIndex = worksheet.lastRow ? worksheet.lastRow.number + 1 : 2;
          worksheet.addImage(imageId, {
            tl: { col: 2, row: rowIndex },
            ext: { width: 50, height: 50 },
          });
        } else {
          // Insert regular data
          rowData.push(answers[i] || "");
        }
      });
      if (rowData.length > 0) {
        worksheet.addRow(rowData);
      }
    }

    // Set workbook properties
    workbook.creator = "Your Name";
    workbook.lastModifiedBy = "Your Name";
    workbook.created = new Date();
    workbook.modified = new Date();

    // Generate Excel file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = "Checklist Response Report.xlsx";
      saveAs(blob, fileName);
    });
  };

  const isBase64 = (str) => {
    if (str.length < 20) return false;

    const base64Pattern =
      /^(?:[A-Za-z0-9+\/]{4})*?(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
    return base64Pattern.test(str);
  };

  const handleImageClick = (imageData) => {
    var w = window.open("about:blank");
    setTimeout(() => {
      w.document.body.appendChild(w.document.createElement("iframe")).src =
        "data:image/png;base64," + imageData;
      w.document.body.style.margin = 0;
      w.document.getElementsByTagName("iframe")[0].style.width = "100%";
      w.document.getElementsByTagName("iframe")[0].style.height = "100%";
      w.document.getElementsByTagName("iframe")[0].style.border = 0;
    }, 0);
  };

  return (
    <div className="px-0 px-md-3">
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <h5 className="mt-4">{t("CheckList Report")}</h5>
        <div className="mb-3 mt-3">
          <Button variant="contained" color="success" onClick={convertToExcel}>
            {t("Export in csv")}
          </Button>
        </div>
      </div>
      <div className="Table mb-6">
        <TableContainer
          component={Paper}
          style={{ boxShadow: "0px 13px 20px 0px #80808029", overflowX: 'auto'  }}
          className="my-4"
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="center">Sr. No</TableCell>
                {answerData.map((val, index) => (
                  <TableCell key={index} align="center">
                    {val.question}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={{ color: "white" }}>
              {answerData.length > 0 ? (
                [...Array(answerData[0].answers.split(",").length)].map(
                  (_, rowIndex) => (
                    <TableRow key={rowIndex}>
                      <TableCell align="center">{rowIndex + 1}</TableCell>
                      {answerData.map((row, colIndex) => (
                        <TableCell key={colIndex} align="center">
                          {row.question_type === 1 &&
                          row.answers.split(",")[rowIndex] ? (
                            <img
                              src={`data:image/png;base64,${
                                row.answers.split(",")[rowIndex]
                              }`}
                              className="border border-3"
                              alt="Checklist"
                              style={{
                                width: "50px",
                                borderRadius: "50%",
                                height: "50px",
                              }}
                              onClick={() =>
                                handleImageClick(
                                  row.answers.split(",")[rowIndex]
                                )
                              }
                            />
                          ) : (
                            row.answers.split(",")[rowIndex]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                )
              ) : (
                // Render "No data available" message if answerData is empty
                <TableRow>
                  <TableCell colSpan={answerData.length + 1} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div>
        <button className="btn btn-primary btn-sm me-2"
        onClick={() => navigate(-1)}
        >
          <ArrowBackIosIcon />
          {t("Go Back")}
        </button>
      </div>
    </div>
  );
};

export default CheckListReport;
